import { ShoppingCart, Wallet, Users, Copy, Share2, Send, IndianRupee } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CompactStatCard, PageHeader, StatsGrid } from "@/components/shared";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  useAccountOrders,
  useMemberProfile,
  useMyWallet,
  useReferralLink,
  useReferralSummary,
  useIncomeSummary,
} from "@/hooks/queries";
import { Skeleton } from "@/components/ui/skeleton";

const statusColor: Record<string, string> = {
  placed: "bg-info/10 text-info border-info/20",
  confirmed: "bg-warning/10 text-warning border-warning/20",
  dispatched: "bg-primary/10 text-primary border-primary/20",
  delivered: "bg-success/10 text-success border-success/20",
  cancelled: "bg-destructive/10 text-destructive border-destructive/20",
};

const AccountOverview = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const { data: ordersData, isLoading: ordersLoading } = useAccountOrders({ pageSize: 20 });
  const { data: wallet, isLoading: walletLoading } = useMyWallet();
  const { data: memberProfile } = useMemberProfile();
  const { data: referralSummary, isLoading: referralLoading } = useReferralSummary();
  const { data: referralLink } = useReferralLink();
  const { data: incomeSummary, isLoading: incomeLoading } = useIncomeSummary();

  const orders = ordersData?.items ?? [];
  const recentOrders = orders.slice(0, 3);
  const activeOrders = orders.filter(
    (o) => o.status !== "delivered" && o.status !== "cancelled",
  ).length;
  const referralCode = referralLink?.referralCode ?? user?.referralCode ?? "—";
  const displayName = user?.name ?? "there";
  const isMemberActive = Boolean(memberProfile?.isMemberActive);

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Welcome, ${displayName}! 👋`}
        description="Here's your account overview."
      />

      <StatsGrid columns={3}>
        <CompactStatCard
          title="Total Orders"
          value={ordersLoading ? "…" : ordersData?.totalItems ?? orders.length}
          icon={ShoppingCart}
        />
        <CompactStatCard title="Active Orders" value={ordersLoading ? "…" : activeOrders} icon={ShoppingCart} />
        <CompactStatCard
          title="Wallet Balance"
          value={walletLoading ? "…" : `₹${wallet?.availableBalance?.toFixed(2) ?? "0.00"}`}
          icon={Wallet}
          iconClassName="bg-gradient-primary"
        />
      </StatsGrid>



      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="font-heading text-lg font-bold">Wallet & P2P Transfer</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {isMemberActive
                ? "Send funds to another member by Member ID, or open your wallet for history and withdrawals."
                : "Activate your Member ID to unlock wallet-to-wallet P2P transfers."}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" asChild>
              <Link to="/account/wallet">Open Wallet</Link>
            </Button>
            {isMemberActive ? (
              <Button className="gap-2" asChild>
                <Link to="/account/wallet">
                  <Send className="h-4 w-4" /> Transfer
                </Link>
              </Button>
            ) : (
              <Button className="gap-2" asChild>
                <Link to="/account/member-id">Activate Member ID</Link>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-gradient-warm border-accent/20">
        <CardContent className="p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="font-heading font-bold text-lg">Refer & Earn</h3>
              <p className="text-sm text-muted-foreground mt-1">Share your referral code and earn commission on every purchase!</p>
              <div className="flex items-center gap-2 mt-3">
                <code className="rounded-lg bg-card px-4 py-2 text-lg font-bold font-heading tracking-widest text-primary border border-border">
                  {referralCode}
                </code>
                <Button
                  size="icon"
                  variant="outline"
                  className="h-10 w-10"
                  onClick={() => {
                    navigator.clipboard.writeText(referralCode);
                    toast({ title: "Referral code copied!" });
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <Button
              className="gap-2 self-start"
              onClick={() => {
                const url = referralLink?.referralUrl ?? window.location.origin;
                navigator.clipboard.writeText(url);
                toast({ title: "Referral link copied!" });
              }}
            >
              <Share2 className="h-4 w-4" /> Share Link
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Recent Orders</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {ordersLoading ? (
            Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)
          ) : recentOrders.length > 0 ? (
            recentOrders.map((order) => (
              <div key={order.id} className="flex items-center justify-between rounded-lg border border-border p-3">
                <div>
                  <p className="text-sm font-semibold">{order.orderId}</p>
                  <p className="text-xs text-muted-foreground">
                    {order.storeName} • {order.itemCount} items • ₹{order.total}
                  </p>
                </div>
                <Badge variant="outline" className={statusColor[order.status]}>{order.status}</Badge>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">No orders yet. Start shopping!</p>
          )}
        </CardContent>
      </Card>

      <div className="space-y-3 pt-2">
        <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">My Earnings</h2>
        <StatsGrid columns={5}>
          <CompactStatCard
            title="Total Earnings"
            value={incomeLoading ? "…" : `₹${(incomeSummary?.totalIncome ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`}
            icon={IndianRupee}
            iconClassName="bg-gradient-primary text-primary-foreground border-primary/20"
          />
          <CompactStatCard
            title="Direct Income"
            value={incomeLoading ? "…" : `₹${(incomeSummary?.directIncome ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`}
            icon={IndianRupee}
            iconClassName="bg-blue-100 text-blue-600 border-blue-200"
          />
          <CompactStatCard
            title="Level Income"
            value={incomeLoading ? "…" : `₹${(incomeSummary?.levelIncome ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`}
            icon={IndianRupee}
            iconClassName="bg-purple-100 text-purple-600 border-purple-200"
          />
          <CompactStatCard
            title="Reward Income"
            value={incomeLoading ? "…" : `₹${(incomeSummary?.rewardIncome ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`}
            icon={IndianRupee}
            iconClassName="bg-yellow-100 text-yellow-600 border-yellow-200"
          />
          <CompactStatCard
            title="Cashback Income"
            value={incomeLoading ? "…" : `₹${(incomeSummary?.cashbackIncome ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`}
            icon={IndianRupee}
            iconClassName="bg-green-100 text-green-600 border-green-200"
          />
        </StatsGrid>
      </div>
    </div>
  );
};

export default AccountOverview;
